import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { BookOpen, Check, ChevronDown, Crown, ExternalLink, FileText, Lock, Sparkles } from "lucide-react";
import { FICHES, RESSOURCES_OFFICIELLES } from "@/data/fiches";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { EASE } from "@/components/landing/Reveal";

const FREE_UNLOCKED = 2;

export default function Formations() {
  const { user } = useAuth();
  const premium = user?.plan === "premium";
  const [openId, setOpenId] = useState(FICHES[0].id);
  const [contents, setContents] = useState([]);
  const [openContent, setOpenContent] = useState(null);

  useEffect(() => {
    api.get("/contents").then((r) => setContents(r.data)).catch(() => {});
  }, []);

  return (
    <div className="mx-auto max-w-3xl space-y-6" data-testid="page-formations">
      <div>
        <h1 className="font-display text-2xl font-extrabold text-slate-900 sm:text-3xl">Formations & Ressources</h1>
        <p className="mt-1 text-sm text-slate-500">Fiches de révision alignées sur les 5 thématiques officielles de l'examen civique.</p>
      </div>

      <div className="space-y-3">
        {FICHES.map((fiche, i) => {
          const locked = !premium && i >= FREE_UNLOCKED;
          const open = openId === fiche.id && !locked;
          return (
            <motion.div
              key={fiche.id}
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45, delay: i * 0.06, ease: EASE }}
              className="card-integra overflow-hidden"
              data-testid={`fiche-${fiche.id}`}
            >
              <button
                onClick={() => !locked && setOpenId(open ? null : fiche.id)}
                className="flex w-full items-center gap-3.5 p-5 text-left"
                data-testid={`fiche-toggle-${fiche.id}`}
              >
                <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${locked ? "bg-slate-100" : "bg-bleu-light"}`}>
                  {locked ? <Lock className="h-4.5 w-4.5 text-slate-400" /> : <BookOpen className="h-4.5 w-4.5 text-bleu" />}
                </span>
                <span className="flex-1">
                  <span className="block text-sm font-bold text-slate-900">{fiche.theme}</span>
                  <span className="text-[11px] text-slate-400">{fiche.points.length} notions clés</span>
                </span>
                {locked ? (
                  <span className="flex items-center gap-1.5 rounded-full bg-amber-50 px-3 py-1 text-[10px] font-bold text-amber-600"><Crown className="h-3 w-3" /> Premium</span>
                ) : (
                  <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
                )}
              </button>
              {open && (
                <div className="border-t border-slate-100 p-5" data-testid={`fiche-content-${fiche.id}`}>
                  <ul className="space-y-3">
                    {fiche.points.map((p) => (
                      <li key={p.title} className="rounded-xl bg-slate-50 p-3.5">
                        <p className="text-[13px] font-bold text-slate-900">{p.title}</p>
                        <p className="mt-1 text-xs leading-relaxed text-slate-600">{p.text}</p>
                      </li>
                    ))}
                  </ul>
                  <div className="mt-4 rounded-xl border border-emeraude/20 bg-emerald-50 p-3.5">
                    <p className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-emeraude"><Sparkles className="h-3 w-3" /> À retenir</p>
                    <ul className="mt-2 space-y-1">
                      {fiche.retenir.map((r) => (
                        <li key={r} className="flex items-start gap-2 text-xs text-slate-700"><Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emeraude" /> {r}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
            </motion.div>
          );
        })}
      </div>

      {!premium && (
        <Link to="/dashboard/parametres" className="flex items-center justify-center gap-2 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-xs font-bold text-amber-700 transition-colors hover:bg-amber-100" data-testid="formations-premium-banner">
          <Crown className="h-4 w-4" /> Débloquez les 5 fiches complètes avec Premium — 7,99 €/mois
        </Link>
      )}

      {contents.length > 0 && (
        <section data-testid="contents-equipe">
          <h2 className="mb-2.5 text-xs font-bold uppercase tracking-wider text-slate-400">Contenus de l'équipe INTEGRA FRANCE</h2>
          <div className="space-y-2.5">
            {contents.map((c) => {
              const open = openContent === c.id;
              return (
                <div key={c.id} className="card-integra overflow-hidden" data-testid="content-item">
                  <button onClick={() => setOpenContent(open ? null : c.id)} className="flex w-full items-center gap-3.5 p-4 text-left" data-testid="content-toggle">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-bleu-light"><FileText className="h-4 w-4 text-bleu" /></span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-[13px] font-bold text-slate-900">{c.title}</span>
                      <span className="text-[11px] text-slate-400">{c.section}</span>
                    </span>
                    <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
                  </button>
                  {open && (
                    <div className="border-t border-slate-100 p-4" data-testid="content-body">
                      <p className="whitespace-pre-line text-xs leading-relaxed text-slate-600">{c.body}</p>
                      {(c.source_label || c.source_url) && (
                        <p className="mt-3 text-[10px] text-slate-400">
                          Source : {c.source_url ? (
                            <a href={c.source_url} target="_blank" rel="noreferrer" className="font-semibold text-bleu hover:underline">{c.source_label || c.source_url}</a>
                          ) : c.source_label}
                          {c.last_checked_at && ` — vérifiée le ${new Date(c.last_checked_at).toLocaleDateString("fr-FR")}`}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      )}

      <section data-testid="ressources-officielles">
        <h2 className="mb-2.5 text-xs font-bold uppercase tracking-wider text-slate-400">Ressources officielles</h2>
        <ul className="space-y-2">
          {RESSOURCES_OFFICIELLES.map((r) => (
            <li key={r.url}>
              <a href={r.url} target="_blank" rel="noreferrer" className="card-integra card-hover flex items-center gap-3.5 p-4" data-testid="ressource-officielle-link">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-bleu-light"><ExternalLink className="h-4 w-4 text-bleu" /></span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[13px] font-bold text-slate-900">{r.label}</span>
                  <span className="block text-[11px] leading-relaxed text-slate-500">{r.desc}</span>
                </span>
              </a>
            </li>
          ))}
        </ul>
        <p className="mt-3 text-[10px] text-slate-400">Seules les sources officielles font foi. Méfiez-vous des sites payants non officiels.</p>
      </section>
    </div>
  );
}
